import { useFormik } from 'formik';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Difficulties, Statuses } from '../../domain/Constants';
import { createCourse, getCourses, updateCourse } from '../../store/courseSlice';
import { getLessons } from '../../store/lessonSlice';
import { getPrograms } from '../../store/programSlice';
import Loading from '../common/Loading';
import BreadcrumbNav from '../curriculum/BreadcrumbNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Combobox } from '../ui/combobox';
import { Switch } from '../ui/switch';
import { IconPlus, IconBook, IconClock } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const LessonCard = ({ lesson }) => (
    <Link
        to={`/curriculum/lessons/${lesson.id}`}
        className="text-primary hover:text-primary/80"
    >
        <Card
            key={lesson.id}
            className="h-full cursor-pointer transition-all duration-200 hover:shadow-lg border-border hover:border-primary/20"
        >
            {lesson.image && (
                <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                    <img
                        src={lesson.image}
                        alt={lesson.name}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    {lesson.name}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <CardDescription className="text-muted-foreground flex items-center gap-1">
                    <IconBook className="h-4 w-4" />
                    {lesson.content_ext?.length ? `${lesson.content_ext?.length} sentences` : 'No sentences found'}
                </CardDescription>
            </CardContent>
        </Card>
    </Link>
);

const CourseForm = () => {
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const loading = useSelector(state => state.course.loading);
    const courses = useSelector(state => state.course.list);
    const lessons = useSelector(state => state.lesson.list);
    const programList = useSelector(state => state.program.list);
    const programOptions = programList.map((item) => { return { value: `${item.id}`, label: item.name } }) || [];
    const fileRef = useRef();
    const navigate = useNavigate();
    const { courseId, programId } = useParams();
    const dispatch = useDispatch();

    // Check if we're editing an existing course
    const isEditing = courseId && courseId !== 'new';
    const currentCourse = isEditing ? courses.find(c => c.id === parseInt(courseId)) : null;

    // Get lessons for this course
    const courseLessons = isEditing ? 
        lessons.filter(l => {
            const lessonCourseId = l.courseId || l.course_id;
            return parseInt(lessonCourseId) === parseInt(courseId);
        }).filter((lesson, index, self) => 
            // Remove duplicates based on lesson ID
            index === self.findIndex(l => l.id === lesson.id)
        ) : [];

    console.log('All lessons:', lessons.length, 'Course lessons:', courseLessons.length, 'for courseId:', courseId);

    useEffect(() => {
        dispatch(getPrograms());
        if (isEditing) {
            dispatch(getCourses());
            dispatch(getLessons(courseId));
        }
    }, [dispatch, isEditing, courseId]);

    // Handle navigation after successful submission
    useEffect(() => {
        if (isSubmitting && !loading) {
            // If we came from a program's courses page (URL has programId)
            if (programId) {
                navigate(`/curriculum/programs/${programId}`);
            } 
            // If editing and we know the program from the course data
            else if (isEditing && currentCourse && currentCourse.program_id) {
                navigate(`/curriculum/programs/${currentCourse.program_id}`);
            } 
            // Otherwise go back to main curriculum page
            else {
                navigate('/curriculum');
            }
            setIsSubmitting(false);
        }
    }, [isSubmitting, loading, navigate, programId, currentCourse, isEditing]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            description: currentCourse ? currentCourse.description : '',
            imageId: currentCourse ? currentCourse.imageId : null,
            name: currentCourse ? currentCourse.name : '',
            difficulty: currentCourse ? currentCourse.difficulty : Difficulties[0].value,
            featured: currentCourse ? currentCourse.featured : false,
            program_id: currentCourse ? `${currentCourse.program_id}` : (programId ? programId : ''),
            status: currentCourse ? currentCourse.status : Statuses[0].value,
        },
        validate: values => {
            const errors = {};
            if (!values.name || values.name.length === 0) {
                errors.name = 'Required';
            }

            if (!values.description || values.description.length === 0) {
                errors.description = 'Required';
            }

            if (!values.difficulty || values.difficulty.length === 0) {
                errors.difficulty = 'Required';
            }

            if (!values.program_id || values.program_id.length === 0) {
                errors.program_id = 'Required';
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onSubmit(values);
            fileRef.current.value = null;
            if (!isEditing) {
                resetForm();
            }
        }
    });

    const handleCancel = (e) => {
        e.preventDefault();
        
        // If we came from a program's courses page (URL has programId)
        if (programId) {
            navigate(`/curriculum/programs/${programId}`);
        } 
        // If editing and we know the program from the course data
        else if (isEditing && currentCourse && currentCourse.program_id) {
            navigate(`/curriculum/programs/${currentCourse.program_id}`);
        } 
        // Otherwise go back to main curriculum page
        else {
            navigate('/curriculum');
        }
    };

    const handleAddLesson = () => {
        navigate(`/curriculum/courses/${courseId}/lessons/new`);
    };

    const onChangeFile = (event) => {
        let newFile = event.target.files[0];
        setFile(newFile);
    }

    const onSubmit = (values) => {
        setIsSubmitting(true);

        if (isEditing) {
            dispatch(updateCourse(file, {
                id: currentCourse.id,
                description: values.description,
                imageId: values.imageId,
                name: values.name,
                featured: values.featured,
                difficulty: values.difficulty,
                program_id: parseInt(values.program_id),
                status: values.status,
            }));
            return;
        }

        dispatch(
            createCourse(
                file,
                {
                    description: values.description,
                    imageId: values.imageId,
                    name: values.name,
                    difficulty: values.difficulty,
                    program_id: parseInt(values.program_id),
                    featured: values.featured,
                    status: values.status,
                }
            )
        );
    };

    if (isEditing && !currentCourse) {
        return <div className="p-6">Loading...</div>;
    }

    const getBreadcrumbItems = () => {
        const items = [{ label: 'Curriculum', href: '/curriculum' }];
        
        // Find program - either from URL params or from current course
        let currentProgramId = programId;
        if (!currentProgramId && currentCourse) {
            currentProgramId = currentCourse.program_id;
        }
        
        if (currentProgramId) {
            const program = programList.find(p => p.id === parseInt(currentProgramId));
            if (program) {
                items.push({ 
                    label: program.name, 
                    href: `/curriculum/programs/${currentProgramId}` 
                });
            }
        }
        
        if (isEditing && currentCourse) {
            // For editing: Curriculum → Program → Course → Edit Course
            items.push({ 
                label: currentCourse.name, 
                href: null 
            });
            items.push({ 
                label: 'Edit Course', 
                href: null 
            });
        } else {
            // For creating: Curriculum → [Program] → Create Course
            items.push({ 
                label: 'Create Course', 
                href: null 
            });
        }
        
        return items;
    };

    return (
        <div className="space-y-6">
            <BreadcrumbNav items={getBreadcrumbItems()} />
            
            <Card>
                <CardHeader>
                    <CardTitle>
                        {isEditing ? 'Edit Course' : 'Create New Course'}
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Name</label>
                            <Input
                                type="text"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                placeholder="Enter course name..."
                            />
                            {formik.touched.name && formik.errors.name && (
                                <div className="text-sm text-destructive">{formik.errors.name}</div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Difficulty</label>
                                <Combobox
                                    options={Difficulties}
                                    value={formik.values.difficulty}
                                    onChange={selected => formik.setFieldValue("difficulty", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select difficulty"
                                />
                                {formik.touched.difficulty && formik.errors.difficulty && (
                                    <div className="text-sm text-destructive">{formik.errors.difficulty}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Program</label>
                                <Combobox
                                    options={programOptions}
                                    value={formik.values.program_id}
                                    onChange={selected => formik.setFieldValue("program_id", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select program"
                                    disabled={true}
                                />
                                {formik.touched.program_id && formik.errors.program_id && (
                                    <div className="text-sm text-destructive">{formik.errors.program_id}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Combobox
                                    options={Statuses}
                                    value={formik.values.status}
                                    onChange={selected => formik.setFieldValue("status", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select status"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Description</label>
                            <textarea
                                rows={4}
                                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                name="description"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                placeholder="Enter course description..."
                            />
                            {formik.touched.description && formik.errors.description && (
                                <div className="text-sm text-destructive">{formik.errors.description}</div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Image</label>
                                <Input
                                    type="file"
                                    name="image"
                                    ref={fileRef}
                                    onChange={onChangeFile}
                                    accept="image/*"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Featured</label>
                                <div className="flex items-center space-x-2 pt-2">
                                    <Switch
                                        name="featured"
                                        checked={formik.values.featured}
                                        onCheckedChange={(checked) => formik.setFieldValue("featured", checked)}
                                    />
                                    <label className="text-sm text-muted-foreground">
                                        Mark as featured course
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-4">
                            <div className="flex items-center space-x-3">
                                <Button
                                    type="submit"
                                    disabled={loading || isSubmitting}
                                >
                                    {isEditing ? "Update" : "Create"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={loading || isSubmitting}
                                >
                                    Cancel
                                </Button>
                            </div>

                            {(loading || isSubmitting) && <Loading />}
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Lessons List Section - Only show when editing existing course */}
            {isEditing && currentCourse && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight">Lessons</h2>
                            <p className="text-muted-foreground">
                                Manage lessons for {currentCourse.name}
                            </p>
                        </div>
                        <Button onClick={handleAddLesson}>
                            <IconPlus className="mr-2 h-4 w-4" />
                            Add Lesson
                        </Button>
                    </div>

                    {courseLessons.length === 0 ? (
                        <div className="text-center py-12">
                            <IconBook className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No lessons yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Start building your course by adding your first lesson
                            </p>
                            <Button onClick={handleAddLesson} variant="outline">
                                <IconPlus className="mr-2 h-4 w-4" />
                                Add First Lesson
                            </Button>
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {courseLessons.map((lesson) => (
                                <LessonCard key={lesson.id} lesson={lesson} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CourseForm; 